
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Profile, Supervisor, UserFormValues } from "../types";

async function callAdmin<T = any>(action: string, payload: Record<string, any> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke('admin-users', {
    body: { action, ...payload },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data as T;
}

export const useUserManagement = () => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [companies, setCompanies] = useState<string[]>([]);
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const res = await callAdmin<{ data: Profile[] }>("list_profiles");
      const data = res.data || [];
      const sorted = [...data].sort((a, b) => {
        const nameA = (a.full_name || "").toLowerCase();
        const nameB = (b.full_name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setProfiles(sorted);
    } catch (error: any) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch user profiles: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await callAdmin<{ data: { company_name: string }[] }>("list_companies");
      const unique = Array.from(new Set((res.data || []).map(u => u.company_name).filter(Boolean)));
      unique.sort();
      setCompanies(unique);
    } catch (error: any) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchSupervisors = async (role: string, companyName: string) => {
    if (!role || !companyName) {
      setSupervisors([]);
      return;
    }
    try {
      const res = await callAdmin<{ data: Supervisor[] }>("list_supervisors", { role, company_name: companyName });
      setSupervisors(res.data || []);
    } catch (error: any) {
      console.error("Error fetching supervisors:", error);
      setSupervisors([]);
    }
  };

  const getCurrentUser = async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) {
      setCurrentUserEmail(data.session.user.email);
    }
  };

  const handleCompanyAdded = (companyName: string) => {
    setCompanies(prev => [...prev, companyName].sort());
  };

  const handleLoginToggle = async (userId: string, enabled: boolean) => {
    try {
      await callAdmin("toggle_login", { user_id: userId, enabled });
      toast.success("Login status updated successfully");
      fetchProfiles();
    } catch (error: any) {
      console.error("Error toggling login status:", error);
      toast.error("Failed to update login status");
    }
  };

  const getSupervisorName = (supervisorId: string | null) => {
    if (!supervisorId) return "—";
    const supervisor = profiles.find(p => p.id === supervisorId);
    return supervisor?.full_name || "—";
  };

  useEffect(() => {
    fetchProfiles();
    fetchCompanies();
    getCurrentUser();
  }, []);

  return {
    profiles,
    loading,
    currentUserEmail,
    companies,
    supervisors,
    fetchProfiles,
    fetchCompanies,
    fetchSupervisors,
    handleCompanyAdded,
    handleLoginToggle,
    getSupervisorName
  };
};
